import axios from 'axios';

// Configuration explicite des URLs
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'https://localhost';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://localhost';
const API_URL = process.env.REACT_APP_API_URL || `${BACKEND_URL}/api`;
const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || `${BACKEND_URL}/uploads`;

console.log('🔧 Configuration URLs:');
console.log('   Frontend:', FRONTEND_URL);
console.log('   Backend:', BACKEND_URL);
console.log('   API:', API_URL);
console.log('   Uploads:', UPLOADS_URL);

// Configuration de base d'axios
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Fonctions utilitaires pour l'authentification
const getAuthToken = () => localStorage.getItem('auth_token');
const getUserInfo = () => {
  const userInfo = localStorage.getItem('user_info');
  return userInfo ? JSON.parse(userInfo) : null;
};

const saveAuthInfo = (token, user) => {
  localStorage.setItem('auth_token', token);
  localStorage.setItem('user_info', JSON.stringify(user));
};

const clearAuthInfo = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_info');
};

// Intercepteur pour les requêtes (avec authentification et logging)
api.interceptors.request.use(
  (config) => {
    // Ajouter le token d'authentification
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log(`🌐 ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ Erreur requête:', error);
    return Promise.reject(error);
  }
);

// Intercepteur pour les réponses (avec gestion d'erreurs et logging)
api.interceptors.response.use(
  (response) => {
    console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  (error) => {
    console.error('❌ Erreur réponse:', error.response?.data || error.message || error);
    
    // Gestion des erreurs d'authentification
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.error('Token invalide ou expiré - Déconnexion');
      clearAuthInfo();
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Gestion globale des autres erreurs
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 404:
          console.error('Ressource non trouvée');
          break;
        case 500:
          console.error('Erreur serveur');
          break;
        default:
          console.error(`Erreur ${status}: ${data?.message || 'Erreur inconnue'}`);
      }
    } else if (error.request) {
      console.error('Pas de réponse du serveur');
    } else {
      console.error('Erreur configuration requête');
    }
    
    return Promise.reject(error);
  }
);

// Services API

// Livres
export const livresAPI = {
  getAll: (params = {}) => api.get('/livres', { params }),
  getById: (id) => api.get(`/livres/${id}`),
  create: (data) => api.post('/livres', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/livres/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/livres/${id}`),
  uploadPhoto: (formData) => api.post('/livres/upload-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  moveToRayonnage: (id, data) => api.put(`/livres/${id}/move`, data),
  bulkMoveToRayonnage: (livreIds, rayonnage_id, motif) => api.post('/livres/bulk-move', { livreIds, rayonnage_id, motif }),
  bulkAddTag: (tagId, filters) => api.post('/livres/bulk-tag', { tagId, filters }),
  bulkAddTagByIds: (tagId, livreIds) => api.post('/livres/bulk-tag-ids', { tagId, livreIds }),
  export: () => api.get('/livres/export', { responseType: 'blob' }),
};

// Auteurs
export const auteursAPI = {
  getAll: (params = {}) => api.get('/auteurs', { params }),
  getById: (id) => api.get(`/auteurs/${id}`),
  create: (data) => api.post('/auteurs', data),
  update: (id, data) => api.put(`/auteurs/${id}`, data),
  delete: (id) => api.delete(`/auteurs/${id}`),
  search: (q) => api.get('/auteurs/search', { params: { q } }),
  getDuplicates: (threshold = 0.8) => api.get('/auteurs/duplicates', { params: { threshold } }),
  merge: (targetId, sourceId) => api.post(`/auteurs/${targetId}/merge`, { sourceId }),
};

// Éditeurs
export const editeursAPI = {
  getAll: (params = {}) => api.get('/editeurs', { params }),
  getById: (id) => api.get(`/editeurs/${id}`),
  create: (data) => api.post('/editeurs', data),
  update: (id, data) => api.put(`/editeurs/${id}`, data),
  delete: (id) => api.delete(`/editeurs/${id}`),
  search: (q) => api.get('/editeurs/search', { params: { q } }),
  getDuplicates: (threshold = 0.8) => api.get('/editeurs/duplicates', { params: { threshold } }),
  merge: (targetId, sourceId) => api.post(`/editeurs/${targetId}/merge`, { sourceId }),
};

// Bibliothèques
export const bibliotequesAPI = {
  getAll: (params = {}) => api.get('/bibliotheques', { params }),
  getById: (id) => api.get(`/bibliotheques/${id}`),
  create: (data) => api.post('/bibliotheques', data),
  update: (id, data) => api.put(`/bibliotheques/${id}`, data),
  delete: (id) => api.delete(`/bibliotheques/${id}`),
};

// Rayonnages
export const rayonnagesAPI = {
  getAll: (params = {}) => api.get('/rayonnages', { params }),
  getById: (id) => api.get(`/rayonnages/${id}`),
  create: (data) => api.post('/rayonnages', data),
  update: (id, data) => api.put(`/rayonnages/${id}`, data),
  delete: (id) => api.delete(`/rayonnages/${id}`),
  getByBibliotheque: (bibliothequeId) => api.get(`/bibliotheques/${bibliothequeId}/rayonnages`),
};

// Tags
export const tagsAPI = {
  getAll: (params = {}) => api.get('/tags', { params }),
  getById: (id) => api.get(`/tags/${id}`),
  create: (data) => api.post('/tags', data),
  update: (id, data) => api.put(`/tags/${id}`, data),
  delete: (id) => api.delete(`/tags/${id}`),
  search: (q) => api.get('/tags', { params: { search: q } }),
};

// Statistiques
export const statsAPI = {
  getGeneral: () => api.get('/stats'),
};

// Utilitaires
export const utilsAPI = {
  healthCheck: () => api.get('/health'),
};

// API d'authentification
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  verifyToken: () => api.get('/auth/verify'),
  updateProfile: (profileData) => api.put('/auth/profile', profileData),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { currentPassword, newPassword }),
  
  // Gestion du tenant
  getTenantInfo: () => api.get('/auth/tenant/info'),
  getTenantUsers: () => api.get('/auth/tenant/users'),
  updateTenant: (tenantData) => api.put('/auth/tenant', tenantData),
  createInvitation: (emailInvite, roleInvite = 'member') => 
    api.post('/auth/tenant/invite', { emailInvite, roleInvite }),
  removeTenantUser: (userId) => api.delete(`/auth/tenant/users/${userId}`),
  
  // Invitations
  acceptInvitation: (invitationToken, userData) => 
    api.post('/auth/accept-invitation', { invitationToken, ...userData }),
  
  // Réinitialisation de mot de passe
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

// Fonctions utilitaires d'authentification
export const authUtils = {
  saveAuthInfo,
  clearAuthInfo,
  getAuthToken,
  getUserInfo,
  
  // Vérifier si l'utilisateur est connecté
  isAuthenticated: () => {
    const token = getAuthToken();
    const user = getUserInfo();
    return !!(token && user);
  },
  
  // Obtenir les informations de l'utilisateur connecté
  getCurrentUser: () => getUserInfo(),
  
  // Obtenir le token d'authentification
  getToken: () => getAuthToken(),
  
  // Déconnecter l'utilisateur
  logout: () => {
    clearAuthInfo();
    window.location.href = '/login';
  }
};

// Export des URLs pour utilisation dans d'autres composants
export { FRONTEND_URL, BACKEND_URL, API_URL, UPLOADS_URL };

export default api;
